<script setup lang="ts">
import {cn} from '@/shadcn/lib/utils'
import {Button} from '@/shadcn/components/ui/button'
import {
  Combobox,
  ComboboxAnchor,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxTrigger,
  ComboboxViewport
} from '@/shadcn/components/ui/combobox'
import {ComboboxVirtualizer, useFilter} from 'reka-ui'
import {Check, ChevronsUpDown, Search, X} from '@lucide/vue'
import { computed, ref, watch } from "vue";

interface Option {
  value: string | number
  label: string
  description?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<{
  options: Option[]
  modelValue?: string | number
  placeholder?: string
  fullwidth?: boolean
  name?: string
  disabled?: boolean
  virtualize?: boolean
  id?: string
}>(), {
  virtualize: true
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void,
}>()

const normalizeValue = (value => {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'string' || typeof value === 'number') {
    return props.options.find(option => String(option.value) === String(value))
  }
  return value
})

const internalModelValue = ref(normalizeValue(props.modelValue))

watch(
  () => props.modelValue,
  (newValue) => {
    internalModelValue.value = normalizeValue(newValue)
  }
)

watch(
  internalModelValue,
  (newValue) => {
    emit('update:modelValue', newValue?.value)
  }
)

watch(
  () => props.options,
  () => {
    if (internalModelValue.value) {
      const found = props.options.find(option => String(option.value) === String(internalModelValue.value.value))
      if (!found) {
        internalModelValue.value = undefined
      }
    }
  }
)

const searchTerm = ref('')

const {contains} = useFilter({sensitivity: 'base'})

const filteredOptions = computed(() => {
  if (!searchTerm.value) return props.options

  return props.options.filter(option => contains(option.label, searchTerm.value))
})
</script>

<template>
  <div :class="{ 'tw:w-full': fullwidth }">
    <input v-if="name" type="hidden" :name="name" :value="internalModelValue?.value" />
    <Combobox v-model="internalModelValue" by="label" :disabled="disabled">
      <ComboboxAnchor as-child>
        <ComboboxTrigger as-child>
          <Button :id="id" variant="outline" :class="['tw:justify-between tw:py-2 tw:h-auto tw:rounded-sm tw:shadow-none', fullwidth && 'tw:w-full']" :disabled="disabled">
            <div class="tw:truncate" :class="{ 'tw:text-zinc-500': !internalModelValue }">
              {{ internalModelValue?.label ?? placeholder ?? 'Select an option' }}
            </div>

            <div class="tw:ml-2 tw:flex tw:items-center tw:gap-1">
              <button type="button" v-if="internalModelValue" @click.stop="internalModelValue = undefined"
                class="tw:rounded-full tw:bg-gray-400 tw:p-0.5 tw:hover:bg-gray-500 tw:cursor-pointer tw:flex tw:items-center">
                <X class="tw:size-2.5 tw:text-white" />
              </button>

              <ChevronsUpDown class="tw:h-4 tw:w-4 tw:shrink-0 tw:opacity-50 tw:flex tw:items-center" />
            </div>
          </Button>
        </ComboboxTrigger>
      </ComboboxAnchor>

      <ComboboxList align="end" class="tw:w-[var(--reka-combobox-trigger-width)]">
        <div class="tw:relative tw:w-full tw:items-center">
          <ComboboxInput v-model="searchTerm"
            class="tw:pl-6 focus-visible:tw:ring-0 tw:border-0 tw:rounded-none tw:h-10"
            placeholder="Search options..." />
          <span class="tw:absolute tw:inset-s-0 tw:inset-y-0 tw:flex tw:items-center tw:justify-center tw:px-3">
            <Search class="tw:size-4 tw:text-muted-foreground" />
          </span>
        </div>

        <ComboboxEmpty>
          No options found.
        </ComboboxEmpty>

        <ComboboxViewport class="tw:max-h-48 tw:overflow-y-auto" :class="filteredOptions.length > 0 && 'tw:p-1'">
          <template v-if="virtualize">
            <ComboboxVirtualizer v-slot="{ option }" :options="filteredOptions" :estimate-size="32"
              :text-content="(opt) => opt.label">
              <ComboboxItem :value="option" class="tw:w-full tw:relative tw:data-[disabled]:text-muted-foreground tw:data-[disabled]:data-[highlighted]:text-muted-foreground tw:data-[disabled]:data-[highlighted]:bg-transparent" :disabled="option.disabled">
                <div class="tw:flex tw:flex-col">
                  <span>{{ option.label }}</span>
                  <span v-if="option.description" class="tw:text-sm tw:text-muted-foreground">{{
                    option.description
                  }}</span>
                </div>
                <ComboboxItemIndicator>
                  <Check :class="cn('tw:ml-auto tw:h-4 tw:w-4')" />
                </ComboboxItemIndicator>
              </ComboboxItem>
            </ComboboxVirtualizer>
          </template>

          <template v-else>
            <ComboboxItem v-for="option in filteredOptions" :key="option.value" :value="option" class="tw:w-full tw:relative" :disabled="option.disabled">
              <div class="tw:flex tw:flex-col">
                <span>{{ option.label }}</span>
                <span v-if="option.description" class="tw:text-sm tw:text-muted-foreground">{{
                  option.description
                  }}</span>
              </div>
              <ComboboxItemIndicator>
                <Check :class="cn('tw:ml-auto tw:h-4 tw:w-4')" />
              </ComboboxItemIndicator>
            </ComboboxItem>
          </template>
        </ComboboxViewport>
      </ComboboxList>
    </Combobox>
  </div>
</template>
